// AudioBuffer → WAV bytes, in the browser.
//
// What the pool stores for a clip is "the clip as heard" (a region render, or
// here a decoded slice), and every consumer of pool objects — Live's import,
// the extension's master mix decoder, the viz proxy — reads PCM WAV. 24-bit
// integer rather than 32-bit float: the renders the extension uploads are
// 24-bit, Live imports it without a second thought, and it is what the
// upload-side WAV parser (extension/src/import/audio-slice.ts) already
// handles. Pure: no DOM, no I/O — decoding is the caller's.

/** A decoded buffer's shape, so this stays testable without a real AudioBuffer. */
export interface PcmSource {
  sampleRate: number;
  numberOfChannels: number;
  length: number;
  getChannelData(channel: number): Float32Array;
}

const BYTES_PER_SAMPLE = 3;
const HEADER_BYTES = 44;

/**
 * Encode `[fromSec, toSec)` of a buffer as 24-bit PCM WAV. Bounds are clamped
 * to the buffer; a window that is empty after clamping yields a header-only
 * file (0 frames), never a throw — the caller decides that is nothing.
 */
export function encodeWav24(source: PcmSource, fromSec = 0, toSec?: number): ArrayBuffer {
  const rate = source.sampleRate;
  const channels = Math.max(1, source.numberOfChannels);
  const first = Math.max(0, Math.min(source.length, Math.round(fromSec * rate)));
  const last = Math.max(first, Math.min(source.length, Math.round((toSec ?? source.length / rate) * rate)));
  const frames = last - first;
  const dataBytes = frames * channels * BYTES_PER_SAMPLE;
  const buf = new ArrayBuffer(HEADER_BYTES + dataBytes);
  const view = new DataView(buf);
  const ascii = (at: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(at + i, s.charCodeAt(i));
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * channels * BYTES_PER_SAMPLE, true);
  view.setUint16(32, channels * BYTES_PER_SAMPLE, true);
  view.setUint16(34, 24, true);
  ascii(36, "data");
  view.setUint32(40, dataBytes, true);

  const data = Array.from({ length: channels }, (_, c) => source.getChannelData(Math.min(c, source.numberOfChannels - 1)));
  let at = HEADER_BYTES;
  for (let i = first; i < last; i++) {
    for (let c = 0; c < channels; c++) {
      const v = Math.max(-1, Math.min(1, data[c]![i] ?? 0));
      // Symmetric scaling: full scale is ±(2^23 − 1), so +1.0 never wraps.
      const s = Math.round(v * 8388607);
      view.setUint8(at, s & 0xff);
      view.setUint8(at + 1, (s >> 8) & 0xff);
      view.setUint8(at + 2, (s >> 16) & 0xff);
      at += 3;
    }
  }
  return buf;
}

/** Frames a window encodes to — what `encodeWav24` will write. */
export function framesOf(source: PcmSource, fromSec = 0, toSec?: number): number {
  const rate = source.sampleRate;
  const first = Math.max(0, Math.min(source.length, Math.round(fromSec * rate)));
  const last = Math.max(first, Math.min(source.length, Math.round((toSec ?? source.length / rate) * rate)));
  return last - first;
}

/**
 * The window [fromSec, toSec) of `buffer` as a new AudioBuffer of the same
 * rate and channels — EXACTLY the window's length, silence where the buffer
 * runs out, so a clip declared over the window loops in step with its
 * siblings rather than over a shorter file (2026-09-08 review).
 */
export function sliceBuffer(buffer: AudioBuffer, fromSec: number, toSec: number): AudioBuffer {
  const rate = buffer.sampleRate;
  const first = Math.round(fromSec * rate);
  const length = Math.max(1, Math.round((toSec - fromSec) * rate));
  const out = new AudioBuffer({ numberOfChannels: buffer.numberOfChannels, length, sampleRate: rate });
  const from = Math.max(0, first);
  const to = Math.min(buffer.length, first + length);
  if (to <= from) return out;
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    out.copyToChannel(buffer.getChannelData(ch).subarray(from, to), ch, from - first);
  }
  return out;
}
